import React, { useEffect, useState } from 'react';
import schwabService, { SchwabQuote } from '../../services/schwabService';
import { tier1EtfLabel, yahooFinanceUrl } from '../../data/tier1EtfCatalog';

interface Props {
  symbol: string;
  sleeveKey?: string;
  onClose: () => void;
}

const EtfSymbolQuickView: React.FC<Props> = ({ symbol, sleeveKey, onClose }) => {
  const [quote, setQuote] = useState<SchwabQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const label = tier1EtfLabel(symbol, sleeveKey);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const q = await schwabService.getQuote(symbol);
        if (!cancelled) setQuote(q);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not load Schwab quote');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="etf-symbol-quick-view-title"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="etf-symbol-quick-view-title" className="text-lg font-semibold text-gray-900">
              {symbol}
            </h2>
            {label ? <p className="text-sm text-gray-600">Tier 1 sleeve: {label}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 space-y-3 text-sm text-gray-800">
          {loading ? <p className="text-gray-500">Loading Schwab quote…</p> : null}
          {error ? (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
              {error}. Use Yahoo Finance below for quote details.
            </p>
          ) : null}
          {!loading && !error && !quote ? (
            <p className="text-gray-500">No quote data returned for this symbol.</p>
          ) : null}
          {quote ? (
            <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
              <dt className="text-gray-500">Name</dt>
              <dd className="col-span-1 font-medium">{quote.description || '—'}</dd>
              <dt className="text-gray-500">Last</dt>
              <dd>
                {(() => {
                  const last =
                    quote.lastPrice ??
                    quote.mark ??
                    quote.regularMarketLastPrice ??
                    null;
                  return last != null ? `$${last.toFixed(2)}` : '—';
                })()}
                {quote.netChange != null ? (
                  <span
                    className={
                      quote.netChange >= 0 ? ' ml-1 text-green-700' : ' ml-1 text-red-700'
                    }
                  >
                    {quote.netChange >= 0 ? '+' : ''}
                    {quote.netChange.toFixed(2)}
                  </span>
                ) : null}
              </dd>
              <dt className="text-gray-500">Exchange</dt>
              <dd>{quote.exchangeName || quote.exchange || '—'}</dd>
              {quote.divYield > 0 ? (
                <>
                  <dt className="text-gray-500">Div yield</dt>
                  <dd>{quote.divYield.toFixed(2)}%</dd>
                </>
              ) : null}
            </dl>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          <a
            href={yahooFinanceUrl(symbol)}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            Yahoo Finance
          </a>
        </div>
      </div>
    </div>
  );
};

export default EtfSymbolQuickView;
